/* eslint-disable react-hooks/exhaustive-deps */
import { MskScore } from "../../models/interfaces/msk-score";
import { User } from "../../models/interfaces/user";
import { WellnessProgram } from "../../models/interfaces/wellness-program";
import { useEffect, useState } from "react";
import { SymmioAccessType } from "../../models/enumerations/symmio-access-type";
import wellnessProgramImg from "../../assets/images/wellness-program.png"
import PortalProgressBar from "../portal-progress-bar/portal-progress-bar";
import UserUtil from "../../utilities/user-util";
import { Button } from "../button/button";
import GrayFooterCard from "../gray-footer-card/gray-footer-card";
import DonutChart from "../charts/donut-chart";
import Badge from "../badge/badge";
import { ReactComponent as CalendarIcon } from "../../assets/icons/outline/icon_calendar.svg";
import { DateTime } from "luxon";
import TallyChart from "../tally-chart/tally-chart";
import { WellnessProgramActivity } from "../../models/enumerations/wellness-program-activity";
import { Utils } from "../../utilities/utils";
import { GroupIds } from "../../models/enumerations/group-ids";
import Card from "../card/card";
import { CardTypes } from "../../models/enumerations/card-types";
import UserCorrectiveProgramsService from "../../utilities/services/corrective-programs-service/user-corrective-programs-service";
import UserMskFocusArticlesUtil from "../../utilities/user-msk-focus-articles-util";
import UserArticle from "../../models/interfaces/articles/user-article";
import ArticleUtils from "../../utilities/article-utils";
import { UserMskFocusArticles } from "../../models/interfaces/articles/user-msk-focus-articles";
import { UserCorrectiveProgram } from "../../models/interfaces/corrective-exercises/user-corrective-program";
import { CorrectiveProgramRoutine } from "../../models/interfaces/corrective-exercises/corrective-program-routine";
import ExerciseModal from "./exercise-modal";
import ArticleModal from "./article-modal";
import WellnessProgramService from "../../utilities/services/wellness-program-service";
import DotScale from "../dot-scale/dot-scale";
import MultiLineChartJS from "../charts/multi-line-chart-js";
import { WeeklyCheckin } from "../../models/interfaces/weekly-checkin/weekly-checkin";
import { createLogger } from "../../utilities/logger";

const log = createLogger("DemoUserProfileWellnessProgram");

const COMPONENT_CLASS = "c-user-profile-wellness-program";

interface DemoUserProfileWellnessProgramProps {
    mskScore?: MskScore;
    allMskScores: MskScore[];
    user: User;
    openLicenseModal?: Function;
    hasAvailableLicenses?: boolean;
    canAssignLicense?: boolean;
}

const DemoUserProfileWellnessProgram: React.FC<DemoUserProfileWellnessProgramProps> = (props: DemoUserProfileWellnessProgramProps) => {
    const { user, mskScore } = props;

    const [wellnessProgram, setWellnessProgram] = useState<WellnessProgram | null>();
    const [isLoadingWellnessProgram, setIsLoadingWellnessProgram] = useState(true);
    const [articlesCurrent, setArticlesCurrent] = useState<UserArticle[] | null>([]);
    const [articlesNext, setArticlesNext] = useState<UserArticle[] | null>([]);
    const [correctiveProgramRoutineCurrent, setCorrectiveProgramRoutineCurrent] = useState<{ correctiveProgram: CorrectiveProgramRoutine | null, extraCorrectiveProgram: CorrectiveProgramRoutine | null } | null>();
    const [correctiveProgramRoutineNext, setCorrectiveProgramRoutineNext] = useState<{ correctiveProgram: CorrectiveProgramRoutine | null, extraCorrectiveProgram: CorrectiveProgramRoutine | null } | null>();
    const [userMskCorrectiveExercises, setUserMskCorrectiveExercises] = useState<UserCorrectiveProgram | null>();
    const [userMskFocusArticles, setUserMskFocusArticles] = useState<UserMskFocusArticles | null>();
    const [openExerciseModal, setOpenExerciseModal] = useState(false);
    const [openArticleModal, setOpenArticleModal] = useState(false);
    const [correctiveProgramRoutineModalData, setCorrectiveProgramRoutineModalData] = useState<CorrectiveProgramRoutine>();
    const [articleModalData, setArticleModalData] = useState<UserArticle>();
    const [latestWeeklyCheckin, setLatestWeeklyCheckin] = useState<WeeklyCheckin | null>(null);
    const [perceivedProgressHistory, setPerceivedProgressHistory] = useState<{ x: string, y: number | null }[]>();
    const [exerciseCapabilityHistory, setExerciseCapabilityHistory] = useState<{ x: string, y: number | null }[]>();
    const [painLevelHistory, setPainLevelHistory] = useState<{ x: string, y: number | null }[]>();

    const toJsDate = (d: any): Date => {
        if (!d) return new Date(0);
        return d.toDate ? d.toDate() : d;
    };

    useEffect(() => {
        const getWellnessProgram = async () => {
            if (user.id) {
                if (user.wellnessProgramId) {
                    const fetched = await WellnessProgramService.get(user.wellnessProgramId);
                    setWellnessProgram(fetched);
                } else {
                    const programs = await WellnessProgramService.getBy([
                        { field: "userId", operator: "==", value: user.id }
                    ], [{ field: "created", direction: "desc" }], 1);
                    if (programs && programs.length > 0) {
                        setWellnessProgram(programs[0]);
                    }
                }
                setIsLoadingWellnessProgram(false);
            }
        };

        getWellnessProgram();
    }, [user.id, user.wellnessProgramId]);

    useEffect(() => {
        const getExercisesAndArticles = async () => {
            if (wellnessProgram?.isCurrent && mskScore && user.id) {
                try {
                    if (wellnessProgram.startDateLocalDateTimeISO) {
                        const exercisesCurrentTemp = await UserUtil.getCorrectiveProgram(mskScore, user.id);
                        setCorrectiveProgramRoutineCurrent(exercisesCurrentTemp);

                        const articlesCurrentTemp = await UserUtil.getUserArticle(mskScore, user.id);
                        setArticlesCurrent(articlesCurrentTemp);
                    }

                    const nextDay = wellnessProgram.startDateLocalDateTimeISO ? wellnessProgram.currentDay + 1 : wellnessProgram.currentDay;

                    const exercisesNextTemp = await UserUtil.getCorrectiveProgram(mskScore, user.id, nextDay);
                    setCorrectiveProgramRoutineNext(exercisesNextTemp);

                    const articlesNextTemp = await UserUtil.getUserArticle(mskScore, user.id, nextDay);
                    setArticlesNext(articlesNextTemp);

                    const userMskCorrectiveExercisesTemp = await UserCorrectiveProgramsService.getCurrentByUserId(user.id);
                    setUserMskCorrectiveExercises(userMskCorrectiveExercisesTemp[0]);

                    if (!userMskCorrectiveExercisesTemp || userMskCorrectiveExercisesTemp.length === 0) {
                        const userMskCorrectiveExercisesExtraTemp = await UserCorrectiveProgramsService.getCurrentExtraByUserId(user.id);
                        setUserMskCorrectiveExercises(userMskCorrectiveExercisesExtraTemp[0]);
                    }

                    const userMskFocusArticlesTemp = await UserMskFocusArticlesUtil.getCurrentByUserId(user.id);
                    setUserMskFocusArticles(userMskFocusArticlesTemp);
                } catch (e) {
                    log.error("Failed to load demo user wellness program activities", {
                        exception: e,
                        subjectUserId: user.id,
                    });
                }
            }
        };

        if (wellnessProgram?.isCurrent && mskScore && user.id) {
            getExercisesAndArticles();
        }
    }, [wellnessProgram, mskScore, user.id]);

    useEffect(() => {
        const setWeeklyCheckinData = () => {
            if (!wellnessProgram) return;

            const weeklyCheckins = wellnessProgram.weeklyCheckins ?? [];

            const mostRecentCompleted = [...weeklyCheckins]
                .reverse()
                .find(x => x.completedMs);

            setLatestWeeklyCheckin(mostRecentCompleted ?? null);

            const pastCheckins = weeklyCheckins
                .filter(c => toJsDate(c.date) < new Date())
                .sort((a, b) => toJsDate(a.date).getTime() - toJsDate(b.date).getTime());

            if (pastCheckins.length === 0) return;

            const perceivedProgress = pastCheckins.map(c => ({
                x: toJsDate(c.date).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "2-digit" }),
                y: c.perceivedProgress ?? null,
            }));

            const exerciseCapability = pastCheckins.map(c => ({
                x: toJsDate(c.date).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "2-digit" }),
                y: c.exerciseCapability ?? null,
            }));

            const painLevel = pastCheckins.map(c => ({
                x: toJsDate(c.date).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "2-digit" }),
                y: c.painLevel ?? null,
            }));

            setPerceivedProgressHistory(perceivedProgress);
            setExerciseCapabilityHistory(exerciseCapability);
            setPainLevelHistory(painLevel);
        };

        setWeeklyCheckinData();
    }, [wellnessProgram]);

    const getTallyMarks = () => {
        if (wellnessProgram) {
            let tallyMarks: any[] = [...wellnessProgram.currentProgress];
            for (let i = 0; i < wellnessProgram.lengthInDays - wellnessProgram.currentProgress.length; i++) {
                tallyMarks.push(null);
            }
            return tallyMarks;
        }
        return Array(30).fill(null);
    }

    const adherence7 = user.lastSevenDays ? Math.round(user.lastSevenDays.filter(x => x === true).length / 7 * 100) : 0;
    const adherence30 = user.lastThirtyDays ? Math.round(user.lastThirtyDays.filter(x => x === true).length / 30 * 100) : 0;
    const allTimeAdherence = user.allTimeAdherence
        ? Math.round(user.allTimeAdherence.totalAdheredDays / user.allTimeAdherence.totalDays * 100)
        : 0;

    const handleExerciseCardClick = (correctiveProgramRoutine: CorrectiveProgramRoutine | null) => {
        if (correctiveProgramRoutine) {
            setCorrectiveProgramRoutineModalData(correctiveProgramRoutine);
            setOpenExerciseModal(true);
        }
    };

    const handleArticleCardClick = (userArticle: UserArticle) => {
        setArticleModalData(userArticle);
        setOpenArticleModal(true);
    };

    const getMovementDonutChart = () => {
        if (userMskCorrectiveExercises) {
            const numberCompleted = userMskCorrectiveExercises.completedDateMs.filter(val => val !== 0).length;
            let groupId = GroupIds.MOVEMENT_HEALTH;
            if (userMskFocusArticles?.focusStatuses.find((focusStatus) => focusStatus.mskFocusAreaGroupId === GroupIds.BREATHING || focusStatus.mskFocusAreaGroupId === GroupIds.MOVEMENT_HEALTH_BREATHING)) {
                groupId = GroupIds.MOVEMENT_HEALTH_BREATHING;
            }
            return (
                <div className="flex">
                    <div className={`${COMPONENT_CLASS}__wellness-program-icon`}>
                        {Utils.getFocusAreaData(groupId).icon}
                    </div>
                    <div className="flex flex-col justify-center items-center max-w-24">
                        <div className={`${COMPONENT_CLASS}__donut-chart`}>
                            <DonutChart
                                percentage={(numberCompleted / userMskCorrectiveExercises.routineDays) * 100}
                                mainTextFontSize="16px"
                                mainText={`${numberCompleted}/${userMskCorrectiveExercises.routineDays}`}
                                fillColor="#43B5F8"
                            />
                        </div>
                        <div className="text-10 font-bold uppercase break-words text-center">
                            {Utils.getFocusAreaData(groupId).title}
                        </div>
                    </div>
                </div>
            )
        }
        return <></>;
    };

    if (isLoadingWellnessProgram) {
        return <div>Loading...</div>;
    }

    return (
        <div className={`${COMPONENT_CLASS}`}>
            {correctiveProgramRoutineModalData &&
                <ExerciseModal programRoutine={correctiveProgramRoutineModalData} openExerciseModal={openExerciseModal} setOpenExerciseModal={setOpenExerciseModal} programProgressCurrent={userMskCorrectiveExercises?.completedDateMs.length} programProgressTotal={userMskCorrectiveExercises?.routineDays} />
            }

            {articleModalData &&
                <ArticleModal userArticle={articleModalData} openArticleModal={openArticleModal} setOpenArticleModal={setOpenArticleModal} />
            }

            {user.symmioAccess !== SymmioAccessType.AppLicense
                ?
                <div className="flex flex-wrap-reverse xl:justify-between justify-center items-center border border-black-gray-5 rounded-xl gap-8">
                    <div className="w-3/5 pl-10">
                        <h2 className="mb-4">Wellness Program</h2>
                        <p className="text-sm font-light">
                            This user does not have an active wellness program. Assign a license to get started.
                        </p>
                        <div className="my-6">
                            {props.hasAvailableLicenses && props.canAssignLicense
                                ?
                                <Button
                                    type="default"
                                    buttonText="Enroll User"
                                    onClick={() => props.openLicenseModal?.()} />
                                :
                                <div className="text-sm font-medium">
                                    No licenses available
                                </div>
                            }
                        </div>
                    </div>

                    <div className="max-w-96 p-6 bg-blue-light" style={{ borderRadius: "0 10px 10px 0" }}>
                        <img src={wellnessProgramImg} alt="Your Wellness Program" />
                    </div>
                </div>
                :
                <div className="flex flex-wrap gap-6">
                    <div className={`${COMPONENT_CLASS}__section-header`}>
                        <div className="flex gap-2 items-center">
                            <div className="font-bold text-10 uppercase">Program Status</div>
                            {!mskScore || mskScore.movementScore?.percentage === null || mskScore.lifestyleScore?.percentage === null
                                ? <Badge text="Pending Assessment" bgColor="#D0D0D3" textColor="#121622" />
                                : wellnessProgram?.startDateLocalDateTimeISO && wellnessProgram.isCurrent
                                    ? <Badge text="Active" bgColor="#00DFBC" textColor="#121622" />
                                    : wellnessProgram?.startDateLocalDateTimeISO && !wellnessProgram.isCurrent
                                        ? <Badge text="Ended" bgColor="#E7514F" textColor="#FFFFFF" />
                                        : <Badge text="Pending" bgColor="#D0D0D3" textColor="#121622" />
                            }
                        </div>

                        <div className="flex gap-6">
                            <div className="flex gap-2 items-center">
                                <div className={`${COMPONENT_CLASS}__icon-calendar`}><CalendarIcon /></div>
                                <div className="font-bold text-10 uppercase">Start Date</div>
                                <div className="font-light text-xs">
                                    {wellnessProgram?.startDateLocalDateTimeISO
                                        ? DateTime.fromISO(wellnessProgram.startDateLocalDateTimeISO).toLocaleString()
                                        : "-"}
                                </div>
                            </div>

                            <div className="flex gap-2 items-center">
                                <div className={`${COMPONENT_CLASS}__icon-calendar`}><CalendarIcon /></div>
                                <div className="font-bold text-10 uppercase">End Date</div>
                                <div className="font-light text-xs">
                                    {wellnessProgram?.expirationDateLocalDateTimeISO
                                        ? DateTime.fromISO(wellnessProgram.expirationDateLocalDateTimeISO).toLocaleString()
                                        : "-"}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col grow gap-6">
                        <GrayFooterCard
                            mainText={user?.lastLoggedIn ? new Date(user.lastLoggedIn).toLocaleDateString() : "-"}
                            subText="Last Logged In"
                            footerText="-"
                        />

                        <GrayFooterCard
                            mainText={user?.dayStreak?.toString() || "0"}
                            subText="Current Streak"
                            footerText={`Best: ${user?.bestDayStreak?.toString() || 0} days`}
                        />
                    </div>

                    <div className={`${COMPONENT_CLASS}__score-section-card justify-center items-center width-410`}>
                        <div className="w-full flex justify-between items-center">
                            <h3 className="m-0">
                                Wellness Program Progress
                            </h3>
                            <div className="text-2xl font-bold">
                                {(((wellnessProgram?.currentProgress.length || 0) / (wellnessProgram?.lengthInDays || 21)) * 100).toFixed(0)}%
                            </div>
                        </div>

                        <div className="w-full my-4">
                            <PortalProgressBar
                                total={wellnessProgram?.lengthInDays || 21}
                                currentProgress={wellnessProgram?.currentProgress.length || 0}
                                headerText=""
                                fillColor="#43B5F8"
                            />
                        </div>

                        <div className="w-full flex flex-wrap gap-2 justify-evenly">
                            {userMskFocusArticles && userMskFocusArticles.focusStatuses.length > 0 &&
                                userMskFocusArticles.focusStatuses.map((focusStatus, index) => {
                                    if (focusStatus.mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH && focusStatus.mskFocusAreaGroupId !== GroupIds.MOVEMENT_HEALTH_BREATHING && focusStatus.mskFocusAreaGroupId !== GroupIds.BREATHING) {
                                        const numberCompleted = focusStatus.completedArticles.filter(article => article.completedDateMs !== 0).length;
                                        return (
                                            <div key={index} className="flex">
                                                <div className={`${COMPONENT_CLASS}__wellness-program-icon`}>
                                                    {Utils.getFocusAreaData(focusStatus.mskFocusAreaGroupId).icon}
                                                </div>
                                                <div className="flex flex-col justify-center items-center max-w-24">
                                                    <div className={`${COMPONENT_CLASS}__donut-chart`}>
                                                        <DonutChart
                                                            percentage={(numberCompleted / focusStatus.numArticles) * 100}
                                                            mainTextFontSize="16px"
                                                            mainText={`${numberCompleted}/${focusStatus.numArticles}`}
                                                            fillColor="#43B5F8"
                                                        />
                                                    </div>
                                                    <div className="text-10 font-bold uppercase break-words text-center">
                                                        {Utils.getFocusAreaData(focusStatus.mskFocusAreaGroupId).title}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return null;
                                })
                            }
                            {getMovementDonutChart()}
                        </div>
                    </div>

                    <div className={`${COMPONENT_CLASS}__score-section-card justify-center items-center width-410`}>
                        <div className="w-full flex justify-between">
                            <h3 className="mb-7">
                                Engagement
                            </h3>
                            <div className="text-2xl font-bold">
                                {(wellnessProgram?.currentProgress.filter(val => val === WellnessProgramActivity.Complete).length || 0)}/{wellnessProgram?.lengthInDays || 21} days
                            </div>
                        </div>

                        <div className="w-full">
                            <TallyChart tallyMarks={getTallyMarks()} />
                        </div>

                        <div className="w-full flex flex-wrap justify-between gap-4">
                            <div className="flex flex-col justify-center items-center">
                                <div className={`${COMPONENT_CLASS}__donut-chart`}>
                                    <DonutChart
                                        percentage={adherence7}
                                        showPercentSymbol={true}
                                        mainTextFontSize="16px"
                                        fillColor="#43B5F8"
                                    />
                                </div>
                                <div className="text-10 font-bold uppercase">
                                    7 Day
                                </div>
                            </div>

                            <div className="flex flex-col justify-center items-center">
                                <div className={`${COMPONENT_CLASS}__donut-chart`}>
                                    <DonutChart
                                        percentage={adherence30}
                                        showPercentSymbol={true}
                                        mainTextFontSize="16px"
                                        fillColor="#43B5F8"
                                    />
                                </div>
                                <div className="text-10 font-bold uppercase">
                                    30 Day
                                </div>
                            </div>

                            <div className="flex flex-col justify-center items-center">
                                <div className={`${COMPONENT_CLASS}__donut-chart`}>
                                    <DonutChart
                                        percentage={allTimeAdherence}
                                        showPercentSymbol={true}
                                        mainTextFontSize="16px"
                                        fillColor="#43B5F8"
                                    />
                                </div>
                                <div className="text-10 font-bold uppercase">
                                    All Time
                                </div>
                            </div>
                        </div>
                    </div>

                    {wellnessProgram?.isCurrent && (
                        <div className="w-full flex flex-wrap gap-6">
                            <div className={`${COMPONENT_CLASS}__score-section-card article-exercise-section`}>
                                <div className="flex flex-col gap-4">
                                    <div>
                                        <h3 style={{ margin: "0" }}>Today</h3>
                                    </div>

                                    {wellnessProgram?.startDateLocalDateTimeISO
                                        ?
                                        (correctiveProgramRoutineCurrent?.correctiveProgram || correctiveProgramRoutineCurrent?.extraCorrectiveProgram || (articlesCurrent && articlesCurrent.length > 0))
                                            ?
                                            <>
                                                {correctiveProgramRoutineCurrent?.correctiveProgram &&
                                                    <div onClick={() => handleExerciseCardClick(correctiveProgramRoutineCurrent.correctiveProgram)}>
                                                        <Card
                                                            correctiveProgramRoutine={correctiveProgramRoutineCurrent.correctiveProgram}
                                                            type={CardTypes.Flow}
                                                            title={(correctiveProgramRoutineCurrent.correctiveProgram.translations as any)?.title || "Daily Exercise"}
                                                            subtitle="Daily Corrective"
                                                            duration={correctiveProgramRoutineCurrent.correctiveProgram.duration ?? 0}
                                                            cancelRedirect={true}
                                                        />
                                                    </div>
                                                }

                                                {correctiveProgramRoutineCurrent?.extraCorrectiveProgram &&
                                                    <div onClick={() => handleExerciseCardClick(correctiveProgramRoutineCurrent.extraCorrectiveProgram)}>
                                                        <Card
                                                            correctiveProgramRoutine={correctiveProgramRoutineCurrent.extraCorrectiveProgram}
                                                            type={CardTypes.Flow}
                                                            title={(correctiveProgramRoutineCurrent.extraCorrectiveProgram.translations as any)?.title || "Daily Exercise"}
                                                            subtitle="Bonus"
                                                            duration={correctiveProgramRoutineCurrent.extraCorrectiveProgram.duration ?? 0}
                                                            cancelRedirect={true}
                                                        />
                                                    </div>
                                                }

                                                {articlesCurrent && articlesCurrent.length > 0 &&
                                                    articlesCurrent.map((userArticle, index) => {
                                                        return (
                                                            <div key={index} onClick={() => handleArticleCardClick(userArticle)}>
                                                                <Card
                                                                    userArticle={userArticle}
                                                                    type={CardTypes.Flow}
                                                                    itemId={index}
                                                                    title={(userArticle.article?.translations as any)?.title || "Article"}
                                                                    subtitle={userArticle.article?.videoId ? "Quick Watch" : "Quick Read"}
                                                                    duration={userArticle.article?.readTimeMin ?? ArticleUtils.getArticleDuration(userArticle.article)}
                                                                    cancelRedirect={true}
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </>
                                            :
                                            <div>
                                                <Card
                                                    imageUrl="public/rest-day.svg"
                                                    type={CardTypes.Rest}
                                                    title="Rest Day"
                                                    subtitle="Take it easy today. Your body will thank you."
                                                    cancelRedirect={true}
                                                />
                                            </div>
                                        :
                                        <p className="text-sm font-light">Wellness Program Not Started</p>
                                    }
                                </div>
                            </div>

                            {wellnessProgram && wellnessProgram.currentProgress.length < wellnessProgram.lengthInDays &&
                                wellnessProgram.currentDay !== wellnessProgram.lengthInDays &&
                                <div className={`${COMPONENT_CLASS}__score-section-card article-exercise-section`}>
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <h3 style={{ margin: "0" }}>Up Next</h3>
                                        </div>

                                        {(correctiveProgramRoutineNext?.correctiveProgram || correctiveProgramRoutineNext?.extraCorrectiveProgram || (articlesNext && articlesNext.length > 0))
                                            ?
                                            <>
                                                {correctiveProgramRoutineNext?.correctiveProgram &&
                                                    <div onClick={() => handleExerciseCardClick(correctiveProgramRoutineNext.correctiveProgram)}>
                                                        <Card
                                                            correctiveProgramRoutine={correctiveProgramRoutineNext.correctiveProgram}
                                                            type={CardTypes.Flow}
                                                            title={(correctiveProgramRoutineNext.correctiveProgram.translations as any)?.title || "Daily Exercise"}
                                                            subtitle="Daily Corrective"
                                                            duration={correctiveProgramRoutineNext.correctiveProgram.duration ?? 0}
                                                            cancelRedirect={true}
                                                        />
                                                    </div>
                                                }

                                                {correctiveProgramRoutineNext?.extraCorrectiveProgram &&
                                                    <div onClick={() => handleExerciseCardClick(correctiveProgramRoutineNext.extraCorrectiveProgram)}>
                                                        <Card
                                                            correctiveProgramRoutine={correctiveProgramRoutineNext.extraCorrectiveProgram}
                                                            type={CardTypes.Flow}
                                                            title={(correctiveProgramRoutineNext.extraCorrectiveProgram.translations as any)?.title || "Daily Exercise"}
                                                            subtitle="Bonus"
                                                            duration={correctiveProgramRoutineNext.extraCorrectiveProgram.duration ?? 0}
                                                            cancelRedirect={true}
                                                        />
                                                    </div>
                                                }

                                                {articlesNext && articlesNext.length > 0 &&
                                                    articlesNext.map((userArticle, index) => {
                                                        return (
                                                            <div key={index} onClick={() => handleArticleCardClick(userArticle)}>
                                                                <Card
                                                                    userArticle={userArticle}
                                                                    type={CardTypes.Flow}
                                                                    itemId={index}
                                                                    title={(userArticle.article?.translations as any)?.title || "Article"}
                                                                    subtitle={userArticle.article?.videoId ? "Quick Watch" : "Quick Read"}
                                                                    duration={userArticle.article?.readTimeMin ?? ArticleUtils.getArticleDuration(userArticle.article)}
                                                                    cancelRedirect={true}
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </>
                                            :
                                            <div>
                                                <Card
                                                    imageUrl="public/rest-day.svg"
                                                    type={CardTypes.Rest}
                                                    title="Rest Day"
                                                    subtitle="Take it easy today. Your body will thank you."
                                                    cancelRedirect={true}
                                                />
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                        </div>
                    )}
                    {((latestWeeklyCheckin && latestWeeklyCheckin.perceivedProgress !== undefined && latestWeeklyCheckin.exerciseCapability !== undefined && latestWeeklyCheckin.painLevel !== undefined) || (perceivedProgressHistory && exerciseCapabilityHistory && painLevelHistory)) &&
                        <div className={`${COMPONENT_CLASS}__weekly-checkin-section`}>
                            <h3>Weekly Check-In</h3>
                            <div className={`${COMPONENT_CLASS}__weekly-checkin-content-wrapper`}>
                                {(latestWeeklyCheckin && latestWeeklyCheckin.perceivedProgress !== undefined && latestWeeklyCheckin.exerciseCapability !== undefined && latestWeeklyCheckin.painLevel !== undefined) &&
                                    <div className={`${COMPONENT_CLASS}__latest-checkin-section`}>
                                        <span className="font-bold text-10 uppercase" style={{ letterSpacing: "0.6px" }}>Last Check-In</span>
                                        <span className="font-light text-12">
                                            {DateTime.fromJSDate(toJsDate(latestWeeklyCheckin.date)).toLocaleString()}
                                        </span>
                                        <hr />
                                        <DotScale
                                            fillColor="#00DFBC"
                                            totalDots={10}
                                            dotScaleValue={latestWeeklyCheckin.perceivedProgress ?? 0}
                                            headerText="Perceived Progress"
                                            bottomLeftLabel="No Progress"
                                            bottomRightLabel="Max Progress"
                                        />
                                        <hr />
                                        <DotScale
                                            fillColor="#43B5F8"
                                            totalDots={10}
                                            dotScaleValue={latestWeeklyCheckin.exerciseCapability}
                                            headerText="Exercise Capability"
                                            bottomLeftLabel="Unable"
                                            bottomRightLabel="Fully Capable"
                                        />
                                        <hr />
                                        <DotScale
                                            fillColor="#E7514F"
                                            totalDots={10}
                                            dotScaleValue={latestWeeklyCheckin.painLevel}
                                            headerText="Pain Level"
                                            bottomLeftLabel="No Pain"
                                            bottomRightLabel="Severe Pain"
                                        />
                                    </div>
                                }
                                {(perceivedProgressHistory && exerciseCapabilityHistory && painLevelHistory) &&
                                    <div className={`${COMPONENT_CLASS}__checkin-history-section`}>
                                        <MultiLineChartJS
                                            showToolTips={true}
                                            datasets={[
                                                {
                                                    label: "Perceived Progress",
                                                    data: perceivedProgressHistory.map(history => history.y),
                                                    borderColor: "#00DFBC",
                                                    backgroundColor: "#00DFBC",
                                                    spanGaps: true
                                                },
                                                {
                                                    label: "Exercise Capability",
                                                    data: exerciseCapabilityHistory.map(history => history.y),
                                                    borderColor: "#43B5F8",
                                                    backgroundColor: "#43B5F8",
                                                    spanGaps: true
                                                },
                                                {
                                                    label: "Pain Level",
                                                    data: painLevelHistory.map(history => history.y),
                                                    borderColor: "#E7514F",
                                                    backgroundColor: "#E7514F",
                                                    spanGaps: true
                                                }
                                            ]}
                                            title="Check-In History"
                                            labels={perceivedProgressHistory.map(history => history.x)}
                                            height={"299px"}
                                            horizontalScrolling={true}
                                            pointRadius={7}
                                            pointBorderWidth={3}
                                            borderWidth={5}
                                            pointFontSize={16}
                                            fontFamily={"pragmatica-extended"}
                                            xLabelFontSize={14}
                                            paddingTop={22}
                                            paddingBottom={0}
                                            maintainAspectRatio={false}
                                            yAxisMax={10}
                                            showDataLabels={false}
                                            xAxisBorder={true}
                                            yAxisBorder={true}
                                            displayYTicks={true}
                                            yStepSize={1}
                                            firstAndLastYTickOnly={true}
                                            xOffSet={true}
                                            titleBottomMargin="0px"
                                            minimumPointDistance={110}
                                        />
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    );
}

export default DemoUserProfileWellnessProgram;